import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useInView, animate } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Heart, Users, HandHeart, Menu, X, Check } from 'lucide-react';

// Animated counter component
function AnimatedCounter({ value, duration = 2 }) {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  useEffect(() => {
    if (isInView) {
      const controls = animate(0, value, {
        duration: duration,
        ease: "easeOut",
        onUpdate: (latest) => setDisplayValue(Math.round(latest))
      });
      return () => controls.stop();
    }
  }, [isInView, value, duration]);
  
  return <span ref={ref}>{displayValue.toLocaleString()}</span>;
}
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_prayers_submitted: 0, total_times_prayed: 0, total_warriors: 0 });
  const [publicPrayers, setPublicPrayers] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Form state
  const [personName, setPersonName] = useState('');
  const [intention, setIntention] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [submitterName, setSubmitterName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchPublicPrayers();
  }, []);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/prayers/stats`);
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchPublicPrayers = async () => {
    try {
      const { data } = await axios.get(`${API}/prayers/public`);
      setPublicPrayers(data);
    } catch (error) {
      console.error('Error fetching public prayers:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!personName.trim() || !intention.trim()) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/prayers/submit`, {
        person_name: personName,
        intention: intention,
        is_public: isPublic,
        submitter_name: submitterName || null
      });
      setSubmitSuccess(true);
      setPersonName('');
      setIntention('');
      setIsPublic(false);
      setSubmitterName('');
      fetchStats();
      if (isPublic) fetchPublicPrayers();
      
      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch (error) {
      console.error('Error submitting prayer:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50 border-b border-[#E6E2D6]/50">
        <div className="px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
          <Link to="/" className="font-heading text-2xl font-semibold text-[#2B2E26]" data-testid="logo">
            Pray Together
          </Link>
          
          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <a href="#submit" className="text-[#5C6056] hover:text-[#2B2E26] transition-colors font-body">
              Submit Prayer
            </a>
            <a href="#how-it-works" className="text-[#5C6056] hover:text-[#2B2E26] transition-colors font-body">
              How It Works
            </a>
            <Link to="/about" className="text-[#5C6056] hover:text-[#2B2E26] transition-colors font-body">
              About
            </Link>
            {user ? (
              <>
                {user.role === 'admin' && (
                  <Link to="/admin" className="text-[#5C6056] hover:text-[#2B2E26] transition-colors font-body" data-testid="admin-link">
                    Admin
                  </Link>
                )}
                <Link 
                  to="/dashboard" 
                  className="btn-primary text-sm py-3 px-6"
                  data-testid="dashboard-link"
                >
                  My Dashboard
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" className="text-[#5C6056] hover:text-[#2B2E26] transition-colors font-body" data-testid="sign-in-link">
                  Sign In
                </Link>
                <Link to="/register" className="btn-primary text-sm py-3 px-6" data-testid="become-warrior-btn">
                  Become a Warrior
                </Link>
              </>
            )}
          </nav>

          {/* Mobile menu button */}
          <button 
            className="md:hidden text-[#2B2E26]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-[#FDFBF7] border-t border-[#E6E2D6] px-6 py-4"
          >
            <nav className="flex flex-col gap-4">
              <a href="#submit" className="text-[#5C6056] py-2" onClick={() => setMobileMenuOpen(false)}>
                Submit Prayer
              </a>
              <a href="#how-it-works" className="text-[#5C6056] py-2" onClick={() => setMobileMenuOpen(false)}>
                How It Works
              </a>
              <Link to="/about" className="text-[#5C6056] py-2" onClick={() => setMobileMenuOpen(false)}>
                About
              </Link>
              {user ? (
                <>
                  {user.role === 'admin' && (
                    <Link to="/admin" className="text-[#5C6056] py-2" onClick={() => setMobileMenuOpen(false)}>
                      Admin Console
                    </Link>
                  )}
                  <Link to="/dashboard" className="btn-primary text-center text-sm py-3" onClick={() => setMobileMenuOpen(false)}>
                    My Dashboard
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-[#5C6056] py-2" onClick={() => setMobileMenuOpen(false)}>
                    Sign In
                  </Link>
                  <Link to="/register" className="btn-primary text-center text-sm py-3" onClick={() => setMobileMenuOpen(false)}>
                    Become a Warrior
                  </Link>
                </>
              )}
            </nav>
          </motion.div>
        )}
      </header>

      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center pt-20"
        style={{
          backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/273d484d-85a8-4eaa-9e45-65aa50be200c/images/89e4d7a669ce83b0fc83884daa6b966de90734cb75bcb405e846f0d00febf69b.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-[#FDFBF7]/60"></div>
        <div className="relative z-10 px-6 md:px-12 lg:px-24 text-center max-w-4xl mx-auto">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-medium text-[#2B2E26] tracking-tight leading-none mb-6"
            data-testid="hero-title"
          >
            Bear One Another's Burdens
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-body text-lg md:text-xl text-[#5C6056] mb-10 max-w-2xl mx-auto"
          >
            Submit the names of those you love, and faithful prayer warriors will lift them up in prayer. Together, we carry each other in faith.
          </motion.p>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a href="#submit" className="btn-accent" data-testid="submit-prayer-cta">
              Submit a Prayer Request
            </a>
            {!user && (
              <Link to="/register" className="btn-secondary" data-testid="join-warrior-cta">
                Join as Prayer Warrior
              </Link>
            )}
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-[#F4F1E9]">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#5A6E4B]/10 mb-4">
                <Heart className="w-8 h-8 text-[#5A6E4B]" />
              </div>
              <p className="font-heading text-5xl font-semibold text-[#2B2E26] mb-2" data-testid="stat-prayers-submitted">
                <AnimatedCounter value={stats.total_prayers_submitted} />
              </p>
              <p className="font-body text-[#5C6056]">Prayer Requests</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#C86A51]/10 mb-4">
                <HandHeart className="w-8 h-8 text-[#C86A51]" />
              </div>
              <p className="font-heading text-5xl font-semibold text-[#2B2E26] mb-2" data-testid="stat-times-prayed">
                <AnimatedCounter value={stats.total_times_prayed} />
              </p>
              <p className="font-body text-[#5C6056]">Prayers Offered</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#5A6E4B]/10 mb-4">
                <Users className="w-8 h-8 text-[#5A6E4B]" />
              </div>
              <p className="font-heading text-5xl font-semibold text-[#2B2E26] mb-2" data-testid="stat-warriors">
                <AnimatedCounter value={stats.total_warriors} />
              </p>
              <p className="font-body text-[#5C6056]">Prayer Warriors</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Submit Prayer Form */}
      <section id="submit" className="py-24 md:py-32 bg-white">
        <div className="px-6 md:px-12 lg:px-24 max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-medium text-[#2B2E26] mb-4" data-testid="submit-section-title">
              Submit a Prayer Request
            </h2>
            <p className="font-body text-[#5C6056]">
              Share the name of someone who needs prayer, and our community will lift them up.
            </p>
          </motion.div>

          {submitSuccess && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mb-8 p-4 bg-[#5A6E4B]/10 rounded-2xl flex items-center gap-3"
              data-testid="submit-success-message"
            >
              <Check className="w-5 h-5 text-[#5A6E4B]" />
              <p className="text-[#5A6E4B] font-body">Prayer request submitted. Thank you for your faith!</p>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8" data-testid="prayer-submit-form">
            <div>
              <input
                type="text"
                placeholder="Name of the person to pray for"
                value={personName}
                onChange={(e) => setPersonName(e.target.value)}
                className="organic-input"
                required
                data-testid="person-name-input"
              />
            </div>
            
            <div>
              <textarea
                placeholder="Prayer intention or reason"
                value={intention}
                onChange={(e) => setIntention(e.target.value)}
                rows={3}
                className="organic-input resize-none"
                required
                data-testid="intention-input"
              />
            </div>
            
            <div>
              <input
                type="text"
                placeholder="Your name (optional)"
                value={submitterName}
                onChange={(e) => setSubmitterName(e.target.value)}
                className="organic-input"
                data-testid="submitter-name-input"
              />
            </div>

            <div className="flex items-center justify-between py-4 border-b border-[#E6E2D6]">
              <Label htmlFor="public-toggle" className="font-body text-[#2B2E26] cursor-pointer">
                Display on public prayer wall
              </Label>
              <Switch
                id="public-toggle"
                checked={isPublic}
                onCheckedChange={setIsPublic}
                data-testid="public-toggle"
              />
            </div>

            <button 
              type="submit" 
              className="btn-primary w-full"
              disabled={submitting}
              data-testid="submit-prayer-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Prayer Request'}
            </button>
          </form>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 md:py-32 bg-[#2B2E26]">
        <div className="px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-medium text-[#F4F1E9] mb-4" data-testid="how-it-works-title">
              How It Works
            </h2>
            <p className="font-body text-[#8A8D86] max-w-xl mx-auto">
              Join our community of prayer warriors and make a difference in someone's life through the power of prayer.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center p-8"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#5A6E4B] text-white font-heading text-2xl font-semibold mb-6">
                1
              </div>
              <h3 className="font-heading text-xl text-[#F4F1E9] mb-3">Sign Up Free</h3>
              <p className="font-body text-[#8A8D86]">
                Create your free account to become a prayer warrior and start making a difference.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center p-8"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#C86A51] text-white font-heading text-2xl font-semibold mb-6">
                2
              </div>
              <h3 className="font-heading text-xl text-[#F4F1E9] mb-3">Receive 5 Names</h3>
              <p className="font-body text-[#8A8D86]">
                You'll be assigned 5 people to pray for. Each name comes with their prayer intention.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center p-8"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#5A6E4B] text-white font-heading text-2xl font-semibold mb-6">
                3
              </div>
              <h3 className="font-heading text-xl text-[#F4F1E9] mb-3">Pray & Continue</h3>
              <p className="font-body text-[#8A8D86]">
                Mark prayers as complete and request 5 more names to continue your ministry.
              </p>
            </motion.div>
          </div>

          {!user && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center mt-12"
            >
              <Link to="/register" className="btn-accent" data-testid="join-now-btn">
                Join Now — It's Free
              </Link>
            </motion.div>
          )}
        </div>
      </section>

      {/* Public Prayer Wall */}
      {publicPrayers.length > 0 && (
        <section className="py-24 bg-[#F4F1E9] overflow-hidden">
          <div className="px-6 md:px-12 lg:px-24 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <h2 className="font-heading text-3xl sm:text-4xl font-medium text-[#2B2E26] mb-4" data-testid="prayer-wall-title">
                Public Prayer Wall
              </h2>
              <p className="font-body text-[#5C6056]">
                Join us in praying for these intentions shared by our community.
              </p>
            </motion.div>
          </div>

          <Marquee gradient={false} speed={30} pauseOnHover>
            {publicPrayers.map((prayer) => (
              <div 
                key={prayer.id} 
                className="mx-4 p-6 bg-white/80 backdrop-blur-sm rounded-2xl border border-[#E6E2D6] min-w-[300px] max-w-[350px]"
                data-testid="prayer-wall-card"
              >
                <p className="font-heading text-xl text-[#2B2E26] mb-2">{prayer.person_name}</p>
                <p className="font-body text-[#5C6056] text-sm mb-3 line-clamp-2">{prayer.intention}</p>
                <div className="flex items-center justify-between text-[#8A8D86] text-xs">
                  {prayer.submitter_name && <span>From {prayer.submitter_name}</span>}
                  <span className="flex items-center gap-1">
                    <Heart size={12} className="fill-[#C86A51] text-[#C86A51]" />
                    {prayer.times_prayed} prayers
                  </span>
                </div>
              </div>
            ))}
          </Marquee>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-[#2B2E26] py-16">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="text-center md:text-left">
              <p className="font-heading text-2xl text-[#F4F1E9] mb-2">Pray Together</p>
              <p className="font-body text-[#8A8D86] text-sm">Bear one another's burdens, and so fulfill the law of Christ.</p>
              <p className="font-body text-[#8A8D86] text-xs mt-1">— Galatians 6:2</p>
            </div>
            <div className="flex gap-8 text-[#8A8D86] font-body text-sm">
              <a href="#submit" className="hover:text-[#F4F1E9] transition-colors">Submit Prayer</a>
              <a href="#how-it-works" className="hover:text-[#F4F1E9] transition-colors">How It Works</a>
              <Link to="/about" className="hover:text-[#F4F1E9] transition-colors">About</Link>
              {!user && (
                <Link to="/register" className="hover:text-[#F4F1E9] transition-colors">Join Us</Link>
              )}
            </div>
          </div>
          <div className="border-t border-[#5C6056]/20 mt-8 pt-8 text-center">
            <p className="text-[#8A8D86] text-xs font-body">
              © {new Date().getFullYear()} Pray Together. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
